import React, { useRef, useState } from 'react';
import './DocumentationUpload.css';

interface DocumentationUploadProps {
  onFilesSelected: (files: File[]) => void;
  maxFiles?: number;
  disabled?: boolean;
}

interface UploadedFile {
  file: File;
  id: string;
}

const DocumentationUpload: React.FC<DocumentationUploadProps> = ({ 
  onFilesSelected, 
  maxFiles = 10,
  disabled = false 
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const generateFileId = () => Math.random().toString(36).substr(2, 9);

  const handleFileSelection = (newFiles: FileList) => {
    const filesArray = Array.from(newFiles);
    const validFiles = filesArray.filter(file => {
      // Check file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size is 50MB.`);
        return false;
      }
      
      // Check file type
      const allowedTypes = [
        'text/plain',
        'text/csv',
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword'
      ];
      
      const isAllowedType = allowedTypes.some(type => file.type === type) ||
                           file.name.toLowerCase().endsWith('.txt') ||
                           file.name.toLowerCase().endsWith('.csv') ||
                           file.name.toLowerCase().endsWith('.pdf') ||
                           file.name.toLowerCase().endsWith('.docx') ||
                           file.name.toLowerCase().endsWith('.doc');

      if (!isAllowedType) {
        alert(`File ${file.name} is not supported. Please upload TXT, CSV, PDF, or Word documents.`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    const newUploadedFiles = validFiles.map(file => ({
      file,
      id: generateFileId()
    }));

    const combinedFiles = [...uploadedFiles, ...newUploadedFiles];
    
    // Limit total files
    if (combinedFiles.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed. Extra files will be ignored.`);
      const limitedFiles = combinedFiles.slice(0, maxFiles);
      setUploadedFiles(limitedFiles);
      onFilesSelected(limitedFiles.map(f => f.file));
    } else {
      setUploadedFiles(combinedFiles);
      onFilesSelected(combinedFiles.map(f => f.file));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelection(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelection(e.target.files);
    }
  };

  const removeFile = (fileId: string) => {
    const updatedFiles = uploadedFiles.filter(f => f.id !== fileId);
    setUploadedFiles(updatedFiles);
    onFilesSelected(updatedFiles.map(f => f.file));
  };

  const clearAllFiles = () => {
    setUploadedFiles([]);
    onFilesSelected([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName: string): string => {
    const ext = fileName.toLowerCase().split('.').pop();
    switch (ext) {
      case 'pdf': return '📄';
      case 'txt': return '📝';
      case 'csv': return '📊';
      case 'doc':
      case 'docx': return '📃';
      default: return '📄';
    }
  };

  return (
    <div className="documentation-upload">
      <div className="upload-header">
        <h3>📚 Upload Documentation</h3>
        <p>Upload documents to help AI understand your KPI codes better</p>
      </div>

      <div 
        className={`upload-zone ${isDragOver ? 'drag-over' : ''} ${disabled ? 'disabled' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <div className="upload-content">
          <div className="upload-icon">📁</div>
          <div className="upload-text">
            <strong>Click to upload</strong> or drag and drop documentation files
          </div>
          <div className="upload-subtitle">
            Supports: TXT, CSV, PDF, Word documents (max 50MB each)
          </div>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".txt,.csv,.pdf,.doc,.docx"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          disabled={disabled}
        />
      </div>

      {uploadedFiles.length > 0 && (
        <div className="uploaded-files">
          <div className="files-header">
            <span>Uploaded Files ({uploadedFiles.length}/{maxFiles})</span>
            <button 
              className="clear-all-btn"
              onClick={clearAllFiles}
              disabled={disabled}
            >
              Clear All
            </button>
          </div>
          
          <div className="files-list">
            {uploadedFiles.map((uploadedFile) => (
              <div key={uploadedFile.id} className="file-item">
                <div className="file-info">
                  <span className="file-icon">{getFileIcon(uploadedFile.file.name)}</span>
                  <div className="file-details">
                    <div className="file-name">{uploadedFile.file.name}</div>
                    <div className="file-size">{formatFileSize(uploadedFile.file.size)}</div>
                  </div>
                </div>
                <button 
                  className="remove-file-btn"
                  onClick={() => removeFile(uploadedFile.id)}
                  disabled={disabled}
                  title="Remove file"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {uploadedFiles.length > 0 && (
        <div className="upload-tips">
          <div className="tip-item">
            💡 <strong>Tip:</strong> Upload KPI definition files, financial manuals, or glossaries to improve AI accuracy
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentationUpload; 